import React, { useEffect, useState } from 'react';
import { Poppins } from 'src/common';
import CheckBox from 'src/components/form/CheckBox';
import styled, { css } from 'styled-components';
import { useFiltersState } from '../Filters/state';
import { useAuth } from 'src/state/auth';
import _ from 'lodash';

const Div = styled.div<{ $borderLeft?: boolean }>`
  display: flex;
  align-items: center;
  padding-left: 20px;
  grid-gap: 8px;
  user-select: none;

  ${({ $borderLeft }) =>
    $borderLeft &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.stroke};
    `}

  .smas-inner {
    display: flex;
    align-items: center;
    cursor: pointer;
    grid-gap: 8px;
  }
`;

interface ShowMyActivitySwitchProps {
  borderLeft?: boolean;
  label: string;
}

export const ShowMyActivitySwitch: React.FC<ShowMyActivitySwitchProps> = ({ borderLeft, label }) => {
  const { filters, setFilters } = useFiltersState();
  const { profile } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [isChecked, setIsChecked] = useState(false);

  const normalizeEmail = (email: string) => email.toLowerCase().trim();

  const extractEmailFromKey = (key: string): string | null => {
    const normalized = normalizeEmail(key);
    const emailPattern = /\(([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})\)/g;
    let match;
    let lastEmail = null;
    while ((match = emailPattern.exec(normalized)) !== null) {
      lastEmail = match[1];
    }
    if (lastEmail) {
      return lastEmail.trim();
    }

    if (normalized.includes('@')) {
      return normalized;
    }
    return null;
  };

  useEffect(() => {
    if (!profile?.user?.email) {
      return;
    }

    if (!filters.owners || Object.keys(filters.owners).length === 0) {
      setIsVisible(false);
      return;
    }

    const userEmail = normalizeEmail(profile.user.email);
    const arr = _.map(filters.owners, (el, key) => {
      const extractedEmail = extractEmailFromKey(key);
      const isMe = extractedEmail === userEmail;
      return { isMe, enabled: el.enabled, key };
    });

    const visible = arr.some((el) => el.isMe);
    const enabledOwners = arr.filter((el) => el.enabled);
    const onlyUserEnabled = enabledOwners.length > 0 && enabledOwners.every((el) => el.isMe);

    setIsVisible(visible);
    setIsChecked(onlyUserEnabled && visible);
  }, [filters.owners, profile]);

  const handleChange = () => {
    if (!profile?.user.email) return;

    const userEmail = normalizeEmail(profile.user.email);

    if (isChecked) {
      setFilters({
        ...filters,
        owners: _.mapValues(filters.owners, (el) => ({ ...el, enabled: true })),
      });
    } else {
      setFilters({
        ...filters,
        owners: _.mapValues(filters.owners, (el, key) => ({
          ...el,
          enabled: extractEmailFromKey(key) === userEmail,
        })),
      });
    }
  };

  if (!isVisible) return null;

  return (
    <Div $borderLeft={borderLeft}>
      <div onClick={handleChange} className="smas-inner">
        <CheckBox isChecked={isChecked} small />
        <Poppins weight={500} px={14} color="davysGrey">
          {label}
        </Poppins>
      </div>
    </Div>
  );
};
